import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/Icon';
import s from './ExceptionCamera.module.css';

export type EvidencePhoto = { id: string; file: File; label: string; capturedAt: string; preview: string };
const GUIDE = ['破损特写', '外箱面单', '整箱全景'];

function watermarkedFile(source: CanvasImageSource, width: number, height: number, lines: [string, string]) {
  const scale = Math.min(1, 1600 / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale)); canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('照片处理不可用');
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  const font = Math.max(17, Math.round(canvas.width / 42));
  const pad = Math.round(font * .7);
  const blockHeight = font * 3.2;
  ctx.fillStyle = 'rgba(0,0,0,.65)'; ctx.fillRect(0, canvas.height - blockHeight, canvas.width, blockHeight);
  ctx.fillStyle = '#fff'; ctx.font = `600 ${font}px sans-serif`;
  ctx.fillText(lines[0], pad, canvas.height - font * 1.75, canvas.width - pad * 2);
  ctx.font = `${Math.round(font * .78)}px sans-serif`;
  ctx.fillText(lines[1], pad, canvas.height - font * .65, canvas.width - pad * 2);
  return new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('照片生成失败')), 'image/jpeg', .86));
}

export function ExceptionCamera({ photos, orderNo, shipmentNo, userName, warehouseName, onAdd, onRemove, onClose }: {
  photos: EvidencePhoto[]; orderNo: string; shipmentNo?: string; userName: string; warehouseName: string;
  onAdd: (photo: EvidencePhoto) => void; onRemove: (id: string) => void; onClose: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState('');
  const [busy, setBusy] = useState(false);
  const [torch, setTorch] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);
  const [selected, setSelected] = useState<EvidencePhoto | null>(null);
  useEffect(() => {
    let alive = true;
    if (!navigator.mediaDevices?.getUserMedia) { setCameraError('当前浏览器不支持实时相机，请使用系统拍照或选择图片。'); return; }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false }).then((media) => {
      if (!alive) { media.getTracks().forEach((track) => track.stop()); return; }
      stream.current = media;
      if (video.current) { video.current.srcObject = media; void video.current.play().catch(() => setCameraError('相机无法启动，请使用系统拍照或选择图片。')); }
      const capabilities = media.getVideoTracks()[0]?.getCapabilities() as MediaTrackCapabilities & { torch?: boolean } | undefined;
      setHasTorch(Boolean(capabilities?.torch));
    }).catch(() => { if (alive) setCameraError('无法打开相机，请允许相机权限，或使用系统拍照 / 选择图片。'); });
    return () => { alive = false; stream.current?.getTracks().forEach((track) => track.stop()); stream.current = null; };
  }, []);
  const add = async (source: CanvasImageSource, width: number, height: number) => {
    if (busy || photos.length >= 9) return;
    setBusy(true); setCameraError('');
    try {
      const at = new Date();
      const label = GUIDE[photos.length] || '其他凭证';
      const when = new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(at);
      const blob = await watermarkedFile(source, width, height, [`${orderNo}${shipmentNo ? ` · ${shipmentNo}` : ''}`, `${userName} · ${when} · ${warehouseName}`]);
      const id = crypto.randomUUID();
      const file = new File([blob], `异常凭证-${label}-${id}.jpg`, { type: 'image/jpeg' });
      onAdd({ id, file, label, capturedAt: at.toISOString(), preview: URL.createObjectURL(file) });
    } catch (cause) { setCameraError(cause instanceof Error ? cause.message : '拍照失败'); }
    finally { setBusy(false); }
  };
  const shoot = () => {
    const node = video.current;
    if (!node?.videoWidth || !node.videoHeight) return setCameraError('相机画面尚未就绪，请稍后再拍。');
    void add(node, node.videoWidth, node.videoHeight);
  };
  const choose = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/') || file.size > 20 * 1024 * 1024) return setCameraError('请选择不超过 20MB 的图片');
    try { const image = await createImageBitmap(file); await add(image, image.width, image.height); image.close(); }
    catch (cause) { setCameraError(cause instanceof Error ? cause.message : '图片读取失败'); }
  };
  const toggleTorch = async () => {
    const track = stream.current?.getVideoTracks()[0];
    if (!track) return;
    try { await track.applyConstraints({ advanced: [{ torch: !torch } as MediaTrackConstraintSet] }); setTorch(!torch); }
    catch { setCameraError('这台设备不支持手电筒控制。'); }
  };
  return <div className={s.camera} role="dialog" aria-modal="true" aria-label="现场拍照">
    <video ref={video} className={s.video} autoPlay muted playsInline />
    <div className={s.top}><button type="button" onClick={onClose} aria-label="返回登记异常">×</button><b>凭证照片 {photos.length} / 9<small>{orderNo} · {shipmentNo || '订单级异常'}</small></b><button type="button" onClick={() => void toggleTorch()} disabled={!hasTorch} aria-label="手电筒" aria-pressed={torch} title={hasTorch ? '切换手电筒' : '当前设备不支持手电筒'}><Icon name="torch" size={22} /></button></div>
    <div className={s.guide}>{GUIDE.map((label, index) => <span key={label} className={photos.length > index ? s.complete : photos.length === index ? s.current : ''}>{photos.length > index ? '✓ ' : ''}{label}</span>)}</div>
    <div className={s.frame} aria-hidden="true" /><div className={s.focus} aria-hidden="true" />
    <div className={s.watermark}>{orderNo}{shipmentNo && ` · ${shipmentNo}`}<small>{userName} · 当前时间 · {warehouseName}</small></div>
    {cameraError && <p className={s.error} role="alert">{cameraError}</p>}
    <div className={s.bottom}><p>照片自动加水印，存进异常凭证</p><div className={s.controls}><button type="button" className={s.stack} onClick={() => setSelected(photos.at(-1) || null)} disabled={!photos.length} aria-label="查看已拍照片">{photos.length ? <img src={photos.at(-1)?.preview} alt="最近拍摄的凭证" /> : '0'}<i>{photos.length}</i></button><button type="button" className={s.shutter} onClick={shoot} disabled={busy || photos.length >= 9} aria-label="拍照"><i /></button>{cameraError && <label className={s.picker}>系统拍照 / 选图<input type="file" accept="image/*" capture="environment" onChange={(event) => { void choose(event.currentTarget.files?.[0] || null); event.currentTarget.value = ''; }} /></label>}<button type="button" className={s.done} onClick={onClose}>完成</button></div></div>
    {selected && <div className={s.preview}><img src={selected.preview} alt={`${selected.label}凭证`} /><div><button type="button" onClick={() => setSelected(null)}>返回相机</button><button type="button" onClick={() => { onRemove(selected.id); setSelected(null); }}>删除这张</button></div></div>}
  </div>;
}
